import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {Auth} from '../actions/auth.actions';
import {Xero} from '../actions/xero.actions';
import {XeroService} from '../services/xero.service';
import {XeroConfiguration} from '../models/xero/xeroConfiguration.model';
import {XeroSyncLog} from '../models/xero/xeroSyncLog.model';
import {XeroAccount} from '../models/xero/xeroAccount.model';
import {XeroTaxRate} from '../models/xero/xeroTaxRate.model';
import {XeroTenant} from '../models/xero/xeroTenant.model';
import ClearState = Auth.ClearState;

export interface XeroStateModel {
  configuration: XeroConfiguration | null;
  authUrl: string | null;
  tenants: XeroTenant[];
  xeroAccounts: XeroAccount[];
  taxRates: XeroTaxRate[];
  syncLogs: XeroSyncLog[];
}

@State<XeroStateModel>({
  name: 'xero',
  defaults: {
    configuration: null,
    authUrl: null,
    tenants: [],
    xeroAccounts: [],
    taxRates: [],
    syncLogs: [],
  }
})
@Injectable()
export class XeroState {
  constructor(private xeroService: XeroService) {}

  @Selector()
  static configuration(state: XeroStateModel): XeroConfiguration | null {
    return state.configuration;
  }

  @Selector()
  static authUrl(state: XeroStateModel): string | null {
    return state.authUrl;
  }

  @Selector()
  static tenants(state: XeroStateModel): XeroTenant[] {
    return state.tenants;
  }

  @Selector()
  static xeroAccounts(state: XeroStateModel): XeroAccount[] {
    return state.xeroAccounts;
  }

  @Selector()
  static taxRates(state: XeroStateModel): XeroTaxRate[] {
    return state.taxRates;
  }

  @Selector()
  static syncLogs(state: XeroStateModel): XeroSyncLog[] {
    return state.syncLogs;
  }

  @Action(Xero.GetConfiguration)
  getConfiguration(ctx: StateContext<XeroStateModel>, action: Xero.GetConfiguration): Observable<XeroConfiguration> {
    if (!action.force && ctx.getState().configuration) {
      return of(ctx.getState().configuration!);
    }
    return this.xeroService.getConfiguration().pipe(tap(result => {
      ctx.patchState({configuration: result});
    }));
  }

  @Action(Xero.CreateConfiguration)
  createConfiguration(ctx: StateContext<XeroStateModel>, action: Xero.CreateConfiguration): Observable<XeroConfiguration> {
    return this.xeroService.createConfiguration(action.request).pipe(tap(result => {
      ctx.patchState({configuration: result});
    }));
  }

  @Action(Xero.UpdateConfiguration)
  updateConfiguration(ctx: StateContext<XeroStateModel>, action: Xero.UpdateConfiguration): Observable<XeroConfiguration> {
    return this.xeroService.updateConfiguration(action.id, action.request).pipe(tap(result => {
      ctx.patchState({configuration: result});
    }));
  }

  @Action(Xero.GetAuthUrl)
  getAuthUrl(ctx: StateContext<XeroStateModel>): Observable<{ url: string }> {
    return this.xeroService.getAuthUrl().pipe(tap(result => {
      ctx.patchState({authUrl: result.url});
    }));
  }

  @Action(Xero.HandleCallback)
  handleCallback(ctx: StateContext<XeroStateModel>, action: Xero.HandleCallback): Observable<XeroConfiguration> {
    return this.xeroService.handleCallback(action.code, action.state).pipe(tap(result => {
      ctx.patchState({configuration: result});
    }));
  }

  @Action(Xero.GetTenants)
  getTenants(ctx: StateContext<XeroStateModel>): Observable<XeroTenant[]> {
    return this.xeroService.getAvailableTenants().pipe(tap(result => {
      ctx.patchState({tenants: result});
    }));
  }

  @Action(Xero.SelectTenant)
  selectTenant(ctx: StateContext<XeroStateModel>, action: Xero.SelectTenant): Observable<XeroConfiguration> {
    return this.xeroService.selectTenant(action.configId, action.tenantId).pipe(tap(result => {
      ctx.patchState({configuration: result});
    }));
  }

  @Action(Xero.GetXeroAccounts)
  getXeroAccounts(ctx: StateContext<XeroStateModel>): Observable<XeroAccount[]> {
    return this.xeroService.getXeroAccounts().pipe(tap(result => {
      ctx.patchState({xeroAccounts: result});
    }));
  }

  @Action(Xero.GetTaxRates)
  getTaxRates(ctx: StateContext<XeroStateModel>): Observable<XeroTaxRate[]> {
    return this.xeroService.getXeroTaxRates().pipe(tap(result => {
      ctx.patchState({taxRates: result});
    }));
  }

  @Action(Xero.GetSyncLogs)
  getSyncLogs(ctx: StateContext<XeroStateModel>, action: Xero.GetSyncLogs): Observable<XeroSyncLog[]> {
    if (!action.force && ctx.getState().syncLogs.length > 0) {
      return of(ctx.getState().syncLogs);
    }
    return this.xeroService.getSyncLogs().pipe(tap(result => {
      ctx.patchState({syncLogs: result});
    }));
  }

  @Action(Xero.SyncContact)
  syncContact(ctx: StateContext<XeroStateModel>, action: Xero.SyncContact): Observable<any> {
    return this.xeroService.syncContact(action.accountId);
  }

  @Action(Xero.SyncQuote)
  syncQuote(ctx: StateContext<XeroStateModel>, action: Xero.SyncQuote): Observable<any> {
    return this.xeroService.syncQuote(action.jobId);
  }

  @Action(Xero.SyncInvoice)
  syncInvoice(ctx: StateContext<XeroStateModel>, action: Xero.SyncInvoice): Observable<any> {
    return this.xeroService.syncInvoice(action.jobId);
  }

  @Action(Xero.SyncBills)
  syncBills(ctx: StateContext<XeroStateModel>, action: Xero.SyncBills): Observable<any> {
    return this.xeroService.syncBills(action.jobId);
  }

  @Action(Xero.SyncCreditNote)
  syncCreditNote(ctx: StateContext<XeroStateModel>, action: Xero.SyncCreditNote): Observable<any> {
    return this.xeroService.syncCreditNote(action.creditNoteId);
  }

  @Action(Xero.RetryFailedSyncs)
  retryFailedSyncs(ctx: StateContext<XeroStateModel>): Observable<void> {
    return this.xeroService.retryFailedSyncs();
  }

  @Action(ClearState)
  clearState(ctx: StateContext<XeroStateModel>): void {
    ctx.setState({
      configuration: null,
      authUrl: null,
      tenants: [],
      xeroAccounts: [],
      taxRates: [],
      syncLogs: [],
    });
  }
}
